import {
  Box,
  Group,
  Loader,
  Modal,
  Progress,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useNgpSamplingAssignmentQueries } from '../../data-access/queries/gp-sampling-assignment.queries';
import type {
  NgpRecapLaboratory,
  NgpRecapRow,
} from '../../data-access/types/gp-sampling-assignment';
import { SAMPLER_UNASSIGNED_COLOR } from '../gp-assignment-map/point-colors';

type Focus = 'hidden' | 'organic' | 'sms' | 'unassigned';
type Scope = 'fr' | 'world';

function aggregateLaboratories(rows: NgpRecapRow[]): NgpRecapLaboratory[] {
  const byKey = new Map<number | 'none', NgpRecapLaboratory>();
  for (const row of rows) {
    for (const laboratory of row.laboratories) {
      const key = laboratory.id ?? 'none';
      const current = byKey.get(key);
      if (current) {
        current.points += laboratory.points;
      } else {
        byKey.set(key, { ...laboratory });
      }
    }
  }
  return [...byKey.values()].sort(
    (left, right) =>
      right.points - left.points ||
      (left.name ?? '').localeCompare(right.name ?? ''),
  );
}

function matchesFocus(row: NgpRecapRow, focus: Focus | null): boolean {
  if (focus === 'unassigned') {
    return row.sampler_id == null;
  }
  if (focus === 'hidden') {
    return row.points_visible < row.points_total;
  }
  if (focus === 'sms') {
    return row.sms_sent < row.points_total;
  }
  if (focus === 'organic') {
    return row.organic_pending > 0;
  }
  return true;
}

function percent(done: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.round((done / total) * 100);
}

interface NgpSamplingRecapModalProps {
  onClose: () => void;
  opened: boolean;
}

export const NgpSamplingRecapModal: FC<NgpSamplingRecapModalProps> = ({
  onClose,
  opened,
}) => {
  const { t } = useTranslation();
  const assignmentQueries = useNgpSamplingAssignmentQueries();
  const recapQuery = useQuery({
    ...assignmentQueries.recap(),
    enabled: opened,
  });

  const [search, setSearch] = useState('');
  const [focus, setFocus] = useState<Focus | null>(null);
  const [scope, setScope] = useState<Scope>('fr');

  const i18n = (key: string, options?: Record<string, unknown>) =>
    t(`ngpAssignment.recap.${key}`, options);

  const laboratoryLabel = (laboratory: NgpRecapLaboratory) =>
    laboratory.name ?? i18n('noLaboratory');

  const samplerLabel = (row: NgpRecapRow) =>
    row.sampler_name ?? i18n('unassignedSampler');

  const allRows = useMemo(
    () => recapQuery.data?.[scope] ?? [],
    [recapQuery.data, scope],
  );

  const metrics = useMemo(() => {
    let points = 0;
    let unassigned = 0;
    let hidden = 0;
    let sms = 0;
    let organic = 0;
    let finished = 0;
    let pending = 0;
    let impossible = 0;
    for (const row of allRows) {
      points += row.points_total;
      hidden += row.points_total - row.points_visible;
      sms += row.points_total - row.sms_sent;
      organic += row.organic_pending;
      finished += row.points_finished;
      pending += row.points_pending;
      impossible += row.points_impossible;
      if (row.sampler_id == null) {
        unassigned += row.points_total;
      }
    }
    return { finished, hidden, impossible, organic, pending, points, sms, unassigned };
  }, [allRows]);

  const laboratories = useMemo(() => aggregateLaboratories(allRows), [allRows]);

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const unassignedLabel = t('ngpAssignment.recap.unassignedSampler').toLowerCase();
    return allRows
      .filter((row) => {
        if (!matchesFocus(row, focus)) {
          return false;
        }
        if (!query) {
          return true;
        }
        const name = (row.sampler_name ?? unassignedLabel).toLowerCase();
        const organization = row.organization?.name?.toLowerCase() ?? '';
        return name.includes(query) || organization.includes(query);
      })
      .sort((left, right) => {
        if ((left.sampler_id == null) !== (right.sampler_id == null)) {
          return left.sampler_id == null ? -1 : 1;
        }
        return right.points_total - left.points_total;
      });
  }, [allRows, focus, search, t]);

  const totals = useMemo(() => {
    const sums = {
      blocked: 0,
      finished: 0,
      hidden: 0,
      impossible: 0,
      organic: 0,
      pending: 0,
      points: 0,
      sms: 0,
    };
    for (const row of rows) {
      sums.points += row.points_total;
      sums.finished += row.points_finished;
      sums.pending += row.points_pending;
      sums.impossible += row.points_impossible;
      sums.hidden += row.points_total - row.points_visible;
      sums.sms += row.points_total - row.sms_sent;
      sums.organic += row.organic_pending;
      sums.blocked += row.organic_blocked;
    }
    return sums;
  }, [rows]);

  const metricItems: { id: Focus | 'points'; label: string; value: number }[] = [
    { id: 'points', label: i18n('metricPoints'), value: metrics.points },
    { id: 'unassigned', label: i18n('metricUnassigned'), value: metrics.unassigned },
    { id: 'hidden', label: i18n('metricHidden'), value: metrics.hidden },
    { id: 'sms', label: i18n('metricSms'), value: metrics.sms },
    { id: 'organic', label: i18n('metricOrganic'), value: metrics.organic },
  ];

  const selectMetric = (id: Focus | 'points') => {
    if (id === 'points') {
      setFocus(null);
      return;
    }
    setFocus((current) => (current === id ? null : id));
  };

  const countColor = (value: number) => (value === 0 ? 'dimmed' : undefined);

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      size="100%"
      styles={{
        body: { maxHeight: 'calc(94vh - 88px)', overflow: 'auto' },
        content: {
          maxHeight: '94vh',
          maxWidth: 'calc(100vw - 32px)',
          width: 'calc(100vw - 32px)',
        },
        header: {
          alignItems: 'center',
          borderBottom: '1px solid var(--mantine-color-gray-2)',
          padding: '18px 20px 14px',
        },
      }}
      title={
        <Stack gap={8}>
          <Text fw={700} lh={1} size="xl">
            {i18n('title')}
          </Text>
          <Box
            style={{
              background: 'var(--mantine-color-pf-green-7)',
              borderRadius: 99,
              height: 3,
              width: 40,
            }}
          />
        </Stack>
      }
    >
      {recapQuery.isLoading ? (
        <Group gap="xs" justify="center" py="xl">
          <Loader size="sm" />
          <Text c="dimmed" size="sm">{i18n('loading')}</Text>
        </Group>
      ) : (
        <Stack gap="md">
          <SegmentedControl
            aria-label={i18n('title')}
            data={[
              { label: i18n('scopeFrance'), value: 'fr' },
              { label: i18n('scopeWorld'), value: 'world' },
            ]}
            onChange={(value) => setScope(value === 'world' ? 'world' : 'fr')}
            value={scope}
          />

          {allRows.length === 0 ? (
            <Text c="dimmed" py="xl" ta="center">{i18n('empty')}</Text>
          ) : (
            <>
              <Group gap={0} grow preventGrowOverflow={false} wrap="wrap">
                {metricItems.map((item) => {
                  const active = item.id === 'points' ? focus == null : focus === item.id;
                  const quiet = item.id !== 'points' && item.value === 0;
                  return (
                    <UnstyledButton
                      aria-pressed={active}
                      disabled={quiet}
                      key={item.id}
                      onClick={() => selectMetric(item.id)}
                      px="sm"
                      py={6}
                      style={{
                        borderBottom: active
                          ? '2px solid var(--mantine-color-pf-green-7)'
                          : '2px solid transparent',
                      }}
                    >
                      <Text
                        c={quiet ? 'dimmed' : undefined}
                        fw={700}
                        lh={1}
                        size="xl"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {item.value}
                      </Text>
                      <Text c="dimmed" mt={4} size="xs">{item.label}</Text>
                    </UnstyledButton>
                  );
                })}
              </Group>

              <Stack gap={6}>
                <Group gap="lg" justify="space-between">
                  <Text c="dimmed" size="xs">{i18n('columnSampled')}</Text>
                  <Group gap="md">
                    <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {metrics.finished} / {metrics.points}
                    </Text>
                    {metrics.pending > 0 ? (
                      <Text c="dimmed" size="sm">
                        {i18n('sampledPending', { count: metrics.pending })}
                      </Text>
                    ) : null}
                    {metrics.impossible > 0 ? (
                      <Text c="red.7" size="sm">
                        {i18n('sampledImpossible', { count: metrics.impossible })}
                      </Text>
                    ) : null}
                  </Group>
                </Group>
                <Progress
                  aria-label={i18n('columnSampled')}
                  color="green"
                  size="sm"
                  value={percent(metrics.finished, metrics.points)}
                />
              </Stack>

              {laboratories.length > 0 ? (
                <Stack gap={8}>
                  <Text c="dimmed" size="xs">{i18n('laboratoriesTitle')}</Text>
                  <Table.ScrollContainer minWidth={Math.max(360, laboratories.length * 120)}>
                    <Table withColumnBorders withTableBorder>
                      <Table.Thead>
                        <Table.Tr>
                          {laboratories.map((laboratory) => (
                            <Table.Th key={laboratory.id ?? 'none'} ta="right">
                              {laboratoryLabel(laboratory)}
                            </Table.Th>
                          ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        <Table.Tr>
                          {laboratories.map((laboratory) => (
                            <Table.Td key={laboratory.id ?? 'none'} ta="right">
                              <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {laboratory.points}
                              </Text>
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </Stack>
              ) : null}

              <TextInput
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder={i18n('search')}
                value={search}
              />

              {rows.length === 0 ? (
                <Text c="dimmed" size="sm">{i18n('empty')}</Text>
              ) : (
                <Table.ScrollContainer maxHeight="55vh" minWidth={1100}>
                  <Table highlightOnHover stickyHeader verticalSpacing="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{i18n('columnSampler')}</Table.Th>
                        <Table.Th ta="right">{i18n('metricPoints')}</Table.Th>
                        <Table.Th style={{ width: 220 }}>{i18n('columnSampled')}</Table.Th>
                        <Table.Th ta="right">{i18n('labelHidden')}</Table.Th>
                        <Table.Th ta="right">{i18n('labelSms')}</Table.Th>
                        <Table.Th ta="right">{i18n('labelOrganic')}</Table.Th>
                        <Table.Th ta="right">{i18n('labelBlocked')}</Table.Th>
                        <Table.Th>{i18n('laboratoriesTitle')}</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {rows.map((row) => {
                        const unassigned = row.sampler_id == null;
                        const hidden = row.points_total - row.points_visible;
                        const sms = row.points_total - row.sms_sent;
                        return (
                          <Table.Tr
                            key={row.sampler_id ?? 'unassigned'}
                            style={unassigned
                              ? { boxShadow: `inset 3px 0 0 ${SAMPLER_UNASSIGNED_COLOR}` }
                              : undefined}
                          >
                            <Table.Td>
                              <Text fw={unassigned ? 600 : 500} size="sm">
                                {samplerLabel(row)}
                              </Text>
                              {row.organization?.name ? (
                                <Text c="dimmed" size="xs">{row.organization.name}</Text>
                              ) : null}
                            </Table.Td>
                            <Table.Td ta="right">
                              <Text fw={700} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                {row.points_total}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={4}>
                                <Group gap="xs" justify="space-between">
                                  <Text fw={600} size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {row.points_finished} / {row.points_total}
                                  </Text>
                                  <Text c="dimmed" size="xs">{percent(row.points_finished, row.points_total)}%</Text>
                                </Group>
                                <Progress
                                  aria-label={i18n('columnSampled')}
                                  color="green"
                                  size="sm"
                                  value={percent(row.points_finished, row.points_total)}
                                />
                                {row.points_pending > 0 || row.points_impossible > 0 ? (
                                  <Group gap="sm">
                                    {row.points_pending > 0 ? (
                                      <Text c="dimmed" size="xs">
                                        {i18n('sampledPending', { count: row.points_pending })}
                                      </Text>
                                    ) : null}
                                    {row.points_impossible > 0 ? (
                                      <Text c="red.7" size="xs">
                                        {i18n('sampledImpossible', { count: row.points_impossible })}
                                      </Text>
                                    ) : null}
                                  </Group>
                                ) : null}
                              </Stack>
                            </Table.Td>
                            <Table.Td c={countColor(hidden)} fw={hidden === 0 ? 400 : 600} ta="right">{hidden}</Table.Td>
                            <Table.Td c={countColor(sms)} fw={sms === 0 ? 400 : 600} ta="right">{sms}</Table.Td>
                            <Table.Td c={countColor(row.organic_pending)} fw={row.organic_pending === 0 ? 400 : 600} ta="right">
                              {row.organic_pending}
                            </Table.Td>
                            <Table.Td c={countColor(row.organic_blocked)} fw={row.organic_blocked === 0 ? 400 : 600} ta="right">
                              {row.organic_blocked}
                            </Table.Td>
                            <Table.Td>
                              <Group gap="md" wrap="wrap">
                                {row.laboratories.map((laboratory) => (
                                  <Group gap={6} key={laboratory.id ?? 'none'} wrap="nowrap">
                                    <Text c="dimmed" size="xs">{laboratoryLabel(laboratory)}</Text>
                                    <Text fw={600} size="sm">{laboratory.points}</Text>
                                  </Group>
                                ))}
                              </Group>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                    <Table.Tfoot>
                      <Table.Tr>
                        <Table.Th>{i18n('total')}</Table.Th>
                        <Table.Th ta="right">{totals.points}</Table.Th>
                        <Table.Th>
                          {totals.finished} / {totals.points}
                        </Table.Th>
                        <Table.Th ta="right">{totals.hidden}</Table.Th>
                        <Table.Th ta="right">{totals.sms}</Table.Th>
                        <Table.Th ta="right">{totals.organic}</Table.Th>
                        <Table.Th ta="right">{totals.blocked}</Table.Th>
                        <Table.Th />
                      </Table.Tr>
                    </Table.Tfoot>
                  </Table>
                </Table.ScrollContainer>
              )}
            </>
          )}
        </Stack>
      )}
    </Modal>
  );
};
